import React, { useState } from 'react';
import { useWeb } from '../context/WebContext';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { FaPhoneAlt, FaWhatsapp, FaEnvelope, FaMapMarkerAlt, FaClock, FaCheck } from 'react-icons/fa';
import toast, { Toaster } from 'react-hot-toast';

export default function Contact() {
  const { locations, settings, addEnquiry, services } = useWeb();
  useDocumentMeta(settings.meta.contact.title, settings.meta.contact.desc);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    location: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.service || !formData.location) {
      toast.error('Please fill in Name, Phone, Service, and City.');
      return;
    }
    addEnquiry(formData);
    toast.success('Thank you! Your enquiry has been received. Our engineer will call you back.');
    setFormData({
      name: '',
      phone: '',
      email: '',
      service: '',
      location: '',
      message: ''
    });
  };

  const handleWhatsappSubmit = () => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'whatsapp_click', {
        'event_category': 'Engagement',
        'event_label': 'WhatsApp Contact Submit'
      });
    }
    const cleanNum = settings.whatsappNumber.replace(/[^0-9]/g, '');
    const text = `Hi Vengamamba Enterprises, My name is ${formData.name || 'Customer'}. I want a quote for ${formData.service || 'Safety Nets'} in ${formData.location || 'my city'}. Phone: ${formData.phone || 'N/A'}.`;
    window.open(`https://api.whatsapp.com/send?phone=${cleanNum}&text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div className="bg-slate-50 py-16 sm:py-24">
      <Toaster position="top-right" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center space-y-3 mb-16">
          <span className="text-xs uppercase bg-orange-50 text-orange-600 font-extrabold px-3 py-1 rounded-full border border-orange-200">
            Get In Touch
          </span>
          <h1 className="text-3xl sm:text-5xl font-black text-slate-900 tracking-tight">
            Contact Vengamamba Safety
          </h1>
          <p className="text-slate-500 max-w-2xl mx-auto text-sm sm:text-base">
            Call or WhatsApp us for instant site measurements and quality estimations.
          </p>
        </div>

        {/* Contact Info Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          
          {/* Details Column */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-6">
              <h3 className="text-xl font-bold text-slate-900">Reach Us</h3>
              
              <div className="space-y-4">
                <div className="flex space-x-4">
                  <div className="bg-orange-50 text-orange-600 p-3.5 rounded-xl text-lg h-12 w-12 flex items-center justify-center flex-shrink-0">
                    <FaPhoneAlt />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-400 uppercase">Primary Phone</h4>
                    <a href={`tel:${settings.phone1}`} onClick={() => gtag_report_conversion()} className="text-slate-800 text-sm font-semibold hover:underline block mt-0.5">{settings.phone1}</a>
                    <a href={`tel:${settings.phone2}`} onClick={() => gtag_report_conversion()} className="text-slate-800 text-sm font-semibold hover:underline block">{settings.phone2}</a>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <div className="bg-orange-50 text-orange-600 p-3.5 rounded-xl text-lg h-12 w-12 flex items-center justify-center flex-shrink-0">
                    <FaEnvelope />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-400 uppercase">Email Support</h4>
                    <a href={`mailto:${settings.email}`} className="text-slate-800 text-sm font-semibold hover:underline block mt-0.5 break-all">{settings.email}</a>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <div className="bg-orange-50 text-orange-600 p-3.5 rounded-xl text-lg h-12 w-12 flex items-center justify-center flex-shrink-0">
                    <FaMapMarkerAlt />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-400 uppercase">Headquarters</h4>
                    <p className="text-slate-600 text-xs sm:text-sm mt-0.5 leading-relaxed">{settings.address}</p>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <div className="bg-orange-50 text-orange-600 p-3.5 rounded-xl text-lg h-12 w-12 flex items-center justify-center flex-shrink-0">
                    <FaClock />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-400 uppercase">Business Hours</h4>
                    <p className="text-slate-600 text-xs sm:text-sm mt-0.5">{settings.workingHours}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Locations List */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-4">
              <h3 className="text-lg font-bold text-slate-900">Regional Coverage</h3>
              <div className="space-y-2">
                {locations.map(loc => (
                  <div key={loc.id} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-b-0">
                    <span className="text-xs sm:text-sm font-semibold text-slate-700">{loc.name}</span>
                    <span className="text-xs font-bold text-orange-600">{loc.phone}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Lead capture Form */}
          <div className="lg:col-span-8">
            <div className="bg-white rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-sm space-y-6">
              <h3 className="text-2xl font-bold text-slate-900">Request Call & Quote Estimation</h3>
              <p className="text-slate-500 text-xs sm:text-sm">Submit your specifications for a free catalog presentation and cost assessment.</p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Your Name *</label>
                    <input 
                      type="text" 
                      placeholder="E.g., Anoop Kumar"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:border-orange-500 outline-none transition"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Phone Number *</label>
                    <input 
                      type="tel" 
                      placeholder="10 digit number"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:border-orange-500 outline-none transition"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Select Net Needed *</label>
                    <select
                      value={formData.service}
                      onChange={(e) => setFormData({...formData, service: e.target.value})}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-3.5 text-sm text-slate-800 outline-none focus:border-orange-500 transition"
                      required
                    >
                      <option value="" disabled>Select Net</option>
                      {services.map(s => (
                        <option key={s.id} value={s.title}>{s.title}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Your City *</label>
                    <select
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-3.5 text-sm text-slate-800 outline-none focus:border-orange-500 transition"
                      required
                    >
                      <option value="" disabled>Select City</option>
                      {locations.map(l => (
                        <option key={l.id} value={l.name}>{l.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Requirements Description</label>
                  <textarea 
                    rows="4" 
                    placeholder="Details about your balcony size, length, width, or specific netting thickness requirements..."
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:border-orange-500 outline-none transition resize-none"
                  ></textarea>
                </div>

                <div className="flex gap-4 pt-2">
                  <button 
                    type="submit" 
                    className="flex-grow bg-orange-600 hover:bg-orange-700 text-white font-bold py-3.5 rounded-xl transition text-center shadow-lg cursor-pointer text-sm"
                  >
                    Request Free Estimate
                  </button>
                  <button 
                    type="button"
                    onClick={handleWhatsappSubmit}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold px-6 py-3.5 rounded-xl transition flex items-center justify-center"
                    title="Send via WhatsApp"
                  >
                    <FaWhatsapp className="text-xl" />
                  </button>
                </div>
              </form>
            </div>
          </div>

        </div>

        {/* Embedded Google Map */}
        <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm h-96">
          <iframe 
            src={settings.mapEmbedUrl}
            width="100%" 
            height="100%" 
            style={{ border: 0 }} 
            allowFullScreen="" 
            loading="lazy" 
            referrerPolicy="no-referrer-when-downgrade"
            title="Google Maps Location"
          ></iframe>
        </div>

      </div>
    </div>
  );
}
